import { notify } from '@/lib/toast';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, Plus, RefreshCw, Trash2, Pencil, QrCode, Copy, ExternalLink, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocationStore } from '@/stores/location.store';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { FilterChips } from '@/components/ui/filter-chips';
import { useAuthStore } from '@/stores/auth.store';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { FloorPlan, RestaurantSection, RestaurantTable } from '@/types/restaurant';

const STATUS_COLORS: Record<string, string> = {
  AVAILABLE: 'bg-success/15 border-success/50 text-success-text',
  OCCUPIED:  'bg-warning/15 border-warning/50 text-warning-text',
  RESERVED:  'bg-primary/10 border-primary/40 text-primary-text',
  CLEANING:  'bg-muted border-border text-muted-foreground',
};


const TABLE_SHAPES = ['SQUARE', 'ROUND', 'RECTANGLE'];

const emptySection = { name: '', color: '#6366f1', sortOrder: 0 };
const emptyTable = { tableNo: '', capacity: 4, sectionId: '', shape: 'SQUARE', locationId: '' };

export function TablesPage(){
  const [activeSection, setActiveSection] = useState<string>('all');
  const { selectedLocationId } = useLocationStore();
  const selectedLocation = selectedLocationId ?? '';
  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null);
  const [sectionsDialogOpen, setSectionsDialogOpen] = useState(false);
  const [addTableOpen, setAddTableOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null);
  const [confirmDeleteTable, setConfirmDeleteTable] = useState<RestaurantTable | null>(null);
  const [tableForm, setTableForm] = useState(emptyTable);
  const [sectionForm, setSectionForm] = useState(emptySection);
  const [editingSection, setEditingSection] = useState<RestaurantSection | null>(null);
  const [confirmDeleteSection, setConfirmDeleteSection] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission, tenant } = useAuthStore();
  const { t } = useTranslation('restaurant');
  const canWrite = hasPermission('restaurant:write') || hasPermission('restaurant:*');
  const STATUS_LABELS: Record<string, string> = {
    AVAILABLE: t('available'),
    OCCUPIED:  t('occupied'),
    RESERVED:  t('reserved'),
    CLEANING:  t('cleaning'),
  };

  // Real-time: refresh floor plan when table status changes (dining order updated)
  const handleTableUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
    queryClient.invalidateQueries({ queryKey: ['restaurant-sections'] });
  }, [queryClient]);
  useSocketEvent('dining_order.created', handleTableUpdate);
  useSocketEvent('dining_order.updated', handleTableUpdate);
  useSocketEvent('dining_order.billed', handleTableUpdate);
  useSocketEvent('dining_order.voided', handleTableUpdate);

  const { data: locations } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['locations'],
    queryFn: () => api.get('/locations').then((r) => r.data?.data ?? r.data ?? []),
  });

  const { data: floorPlan, isLoading } = useQuery<FloorPlan>({
    queryKey: ['floor-plan', selectedLocation],
    queryFn: () => {
      const url = selectedLocation ? `/restaurant/floor-plan?locationId=${selectedLocation}` : '/restaurant/floor-plan';
      return api.get(url).then((r) => r.data);
    },
    refetchInterval: 300000, // 5 min safety-net; real-time via WebSocket
  });

  const { data: sections } = useQuery<RestaurantSection[]>({
    queryKey: ['restaurant-sections', selectedLocation],
    queryFn: () => {
      const url = selectedLocation ? `/restaurant/sections?locationId=${selectedLocation}` : '/restaurant/sections';
      return api.get(url).then((r) => r.data);
    },
  });

  const saveSectionMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      editingSection
        ? api.patch(`/restaurant/sections/${editingSection.id}`, data)
        : api.post('/restaurant/sections', data),
    onSuccess: () => {
      toast.success(editingSection ? t('sectionUpdated') : t('sectionCreated'));
      queryClient.invalidateQueries({ queryKey: ['restaurant-sections'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      setEditingSection(null);
      setSectionForm(emptySection);
    },
    onError: (err) => notify.error(err, t('failedToSaveSection')),
  });

  const saveTableMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      editingTable
        ? api.patch(`/restaurant/tables/${editingTable.id}`, data)
        : api.post('/restaurant/tables', data),
    onSuccess: () => {
      toast.success(editingTable ? t('tableUpdated') : t('tableCreated'));
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      queryClient.invalidateQueries({ queryKey: ['restaurant-tables'] });
      setAddTableOpen(false);
      setEditingTable(null);
      setTableForm(emptyTable);
      setSelectedTable(null);
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err?.response?.data?.message ?? t('failedToSaveTable')),
  });

  const createOrderMutation = useMutation({
    mutationFn: (data: { type: string; tableId: string; guestCount: number }) =>
      api.post('/dining-orders', data),
    onSuccess: (res: { data: { id: string } }) => {
      toast.success(t('orderCreated'));
      queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      setSelectedTable(null);
      navigate(`/restaurant/orders/${res.data.id}`);
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err?.response?.data?.message ?? t('failedToCreateOrder')),
  });

  const deleteTableMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/restaurant/tables/${id}`),
    onSuccess: () => {
      toast.success(t('tableDeleted'));
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      queryClient.invalidateQueries({ queryKey: ['restaurant-tables'] });
      setConfirmDeleteTable(null);
      setSelectedTable(null);
    },
    onError: (err: { response?: { data?: { message?: string } } }) =>
      toast.error(err?.response?.data?.message ?? t('failedToDelete')),
  });

  const deleteSectionMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/restaurant/sections/${id}`),
    onSuccess: () => {
      toast.success(t('sectionDeleted'));
      queryClient.invalidateQueries({ queryKey: ['restaurant-sections'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      setConfirmDeleteSection(null);
    },
    onError: () => toast.error(t('failedToDeleteSection')),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/restaurant/tables/${id}/status`, { status }),
    onSuccess: () => {
      toast.success(t('tableStatusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      queryClient.invalidateQueries({ queryKey: ['restaurant-tables'] });
      queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
      setSelectedTable(null);
    },
    onError: () => toast.error(t('failedToUpdateStatus')),
  });

  const allTables: RestaurantTable[] = [
    ...(floorPlan?.sections.flatMap((s) => s.tables) ?? []),
    ...(floorPlan?.unassigned ?? []),
  ];

  const displayedTables =
    activeSection === 'all'
      ? allTables
      : activeSection === 'unassigned'
      ? (floorPlan?.unassigned ?? [])
      : (floorPlan?.sections.find((s) => s.id === activeSection)?.tables ?? []);

  const sectionTabs = [
    { id: 'all', label: t('allTables') },
    ...(floorPlan?.sections.map((s) => ({ id: s.id, label: s.name })) ?? []),
    ...(floorPlan?.unassigned.length ? [{ id: 'unassigned', label: t('unassigned') }] : []),
  ];

  return (
    <div className="space-y-6 pb-24 lg:pb-6 page-enter">
      <PageHeader
        title={t('floorPlan')}
        subtitle={t('floorPlanSubtitle')}
        actions={
          <>
            {canWrite && (
              <Button variant="outline" onClick={() => setSectionsDialogOpen(true)}>
                Manage Sections
              </Button>
            )}
            {canWrite && (
              <Button onClick={() => {
                setEditingTable(null);
                setTableForm({ ...emptyTable, locationId: selectedLocation });
                setAddTableOpen(true);
              }}>
                <Plus className="me-2 h-4 w-4" />
                Add Table
              </Button>
            )}
          </>
        }
      />

      <FilterChips
        chips={sectionTabs.map((tab) => ({ key: tab.id, label: tab.label }))}
        value={activeSection}
        onChange={setActiveSection}
      />

      {/* Tables grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : displayedTables.length === 0 ? (
        <EmptyState
          icon={LayoutGrid}
          title={t('noTables')}
          description={canWrite ? 'Add your first table using the button above.' : 'No tables in this section.'}
          action={canWrite ? { label: 'Add Table', onClick: () => { setTableForm(emptyTable); setAddTableOpen(true); } } : undefined}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {displayedTables.map((table) => (
            <button
              key={table.id}
              onClick={() => setSelectedTable(table)}
              className={`min-h-20 rounded-xl border-2 p-4 text-start transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${STATUS_COLORS[table.status] ?? STATUS_COLORS.AVAILABLE}`}
            >
              <p className="text-lg font-bold">#{table.tableNo}</p>
              <p className="text-xs opacity-70 flex items-center gap-1">
                <Users className="h-4 w-4 shrink-0" aria-hidden="true" />
                {table.capacity} {t('seatsLabel')}
              </p>
              <span className="mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-background/40">
                {STATUS_LABELS[table.status]}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Add Table dialog */}
      <Dialog
        visible={addTableOpen}
        onHide={() => { setAddTableOpen(false); setEditingTable(null); setTableForm(emptyTable); }}
        header={editingTable ? t('editTable') : t('addTable')}
        className="w-full max-w-lg"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">{t('tableNumber')} <span className="text-destructive-text" aria-hidden="true">*</span></Label>
              <Input
                placeholder={t('tableNoPlaceholder')}
                value={tableForm.tableNo}
                onChange={(e) => setTableForm({ ...tableForm, tableNo: e.target.value })}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">{t('capacitySeats')}</Label>
              <Input
                type="number"
                min="1"
                value={tableForm.capacity}
                onChange={(e) => setTableForm({ ...tableForm, capacity: Number(e.target.value) })}
              />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t('sectionLabel2')}</Label>
            <Dropdown
              className="w-full"
              value={tableForm.sectionId}
              onChange={(e) => setTableForm({ ...tableForm, sectionId: e.value })}
              options={[
                { label: t('unassigned'), value: '' },
                ...(sections ?? []).map((s) => ({ label: s.name, value: s.id })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('unassigned')}
            />
            {(sections ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                {t('addSectionPrompt')}{' '}
                <Link
                  to="/admin/master-data/restaurant-setup"
                  className="text-primary-text font-medium underline hover:no-underline"
                >
                  {t('addSectionLink')}
                </Link>
              </p>
            )}
          </div>
          <div>
            <Label className="mb-1.5 block">{t('locationOptional')}</Label>
            <Dropdown
              className="w-full"
              value={tableForm.locationId}
              onChange={(e) => setTableForm({ ...tableForm, locationId: e.value })}
              options={[
                { label: t('noSpecificLocation'), value: '' },
                ...(locations ?? []).map((l) => ({ label: l.name, value: l.id })),
              ]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('noSpecificLocation')}
            />
          </div>
          <div>
            <Label className="mb-1.5 block">{t('shapeLabel')}</Label>
            <Dropdown
              className="w-full"
              value={tableForm.shape}
              onChange={(e) => setTableForm({ ...tableForm, shape: e.value })}
              options={TABLE_SHAPES.map((s) => ({ label: s.charAt(0) + s.slice(1).toLowerCase(), value: s }))}
              optionLabel="label"
              optionValue="value"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setAddTableOpen(false); setEditingTable(null); setTableForm(emptyTable); }}>
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              disabled={saveTableMutation.isPending || !tableForm.tableNo}
              onClick={() => {
                const payload: Record<string, unknown> = {
                  ...tableForm,
                  capacity: Number(tableForm.capacity) || 1,
                };
                if (!payload.sectionId) delete payload.sectionId;
                if (!payload.locationId) delete payload.locationId;
                saveTableMutation.mutate(payload);
              }}
            >
              {saveTableMutation.isPending
                ? t('saving')
                : editingTable
                ? t('save')
                : t('createTable')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Table detail dialog */}
      <Dialog
        visible={!!selectedTable}
        onHide={() => setSelectedTable(null)}
        header={selectedTable ? t('tableNo', { no: selectedTable.tableNo }) : ''}
        className="w-full max-w-md"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        {selectedTable && (() => {
          const table =
            allTables.find((t) => t.id === selectedTable.id) ?? selectedTable;
          const menuUrl = tenant?.slug
            ? `${window.location.origin}/menu?slug=${tenant.slug}&table=${table.id}`
            : null;
          const qrUrl = menuUrl
            ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(menuUrl)}`
            : null;

          const openEditTable = () => {
            setEditingTable(table);
            setTableForm({
              tableNo: table.tableNo,
              capacity: table.capacity,
              sectionId: table.sectionId ?? '',
              shape: table.shape,
              locationId: table.locationId ?? '',
            });
            setAddTableOpen(true);
          };

          return (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-sm font-medium border ${STATUS_COLORS[table.status]}`}>
                {STATUS_LABELS[table.status]}
              </span>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Users className="h-4 w-4 shrink-0" aria-hidden="true" />
                {table.capacity} {t('seatsLabel')}
              </span>
            </div>
            {table.section && (
              <p className="text-sm text-muted-foreground">{t('sectionLabel', { name: table.section.name })}</p>
            )}

            {/* QR Self-Ordering */}
            {menuUrl && (
              <div className="rounded-xl border p-4 bg-muted/30 text-center space-y-3">
                <p className="text-sm font-semibold flex items-center justify-center gap-2">
                  <QrCode className="h-4 w-4" />
                  QR Self-Ordering
                </p>
                <img
                  src={qrUrl!}
                  alt={`QR code for Table #${table.tableNo}`}
                  className="mx-auto rounded-lg"
                  width={160}
                  height={160}
                />
                <p className="text-xs text-muted-foreground break-all">{menuUrl}</p>
                <div className="flex gap-2 justify-center">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { navigator.clipboard.writeText(menuUrl); toast.success('Link copied!'); }}
                  >
                    <Copy className="h-3 w-3 me-1" />
                    Copy Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(menuUrl, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 me-1" />
                    Preview
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {table.status === 'AVAILABLE' && canWrite && (
                <Button
                  className="w-full h-11"
                  disabled={createOrderMutation.isPending}
                  onClick={() => {
                    createOrderMutation.mutate({
                      type: 'DINE_IN',
                      tableId: table.id,
                      guestCount: Math.max(1, table.capacity),
                    });
                  }}
                >
                  {createOrderMutation.isPending ? t('creating') : t('newOrder')}
                </Button>
              )}
              {table.status === 'OCCUPIED' && (
                <Button
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => {
                    if (table.currentOrderId) {
                      navigate(`/restaurant/orders/${table.currentOrderId}`);
                      setSelectedTable(null);
                    } else {
                      toast.error(t('noOpenOrderForTable'));
                    }
                  }}
                >
                  {t('viewOrder')}
                </Button>
              )}
              {canWrite && (
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 h-10" onClick={openEditTable}>
                    <Pencil className="me-2 h-4 w-4" />
                    {t('editTable')}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 h-10 text-destructive-text border-destructive/30 hover:bg-destructive/10"
                    onClick={() => setConfirmDeleteTable(table)}
                  >
                    <Trash2 className="me-2 h-4 w-4" />
                    {t('deleteTable')}
                  </Button>
                </div>
              )}
              {canWrite && (
                <div className="grid grid-cols-2 gap-2">
                  {(['AVAILABLE', 'OCCUPIED', 'RESERVED', 'CLEANING'] as const).map((s) =>
                    s !== table.status ? (
                      <Button
                        key={s}
                        variant="outline"
                        size="sm"
                        className="text-xs h-9"
                        disabled={updateStatusMutation.isPending}
                        onClick={() => updateStatusMutation.mutate({ id: table.id, status: s })}
                      >
                        → {STATUS_LABELS[s]}
                      </Button>
                    ) : null
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3" />
              {t('autoRefreshes')}
            </div>
          </div>
          );
        })()}
      </Dialog>

      {/* Manage Sections dialog */}
      <Dialog
        visible={sectionsDialogOpen}
        onHide={() => { setSectionsDialogOpen(false); setEditingSection(null); setSectionForm(emptySection); }}
        header={t('manageSections')}
        className="w-full max-w-lg"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          {(sections ?? []).map((sec) => (
            <div key={sec.id} className="flex items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full flex-shrink-0" style={{ backgroundColor: sec.color }} />
                <span className="font-medium">{sec.name}</span>
                <span className="text-xs text-muted-foreground">{sec._count?.tables ?? 0} {t('tables')}</span>
              </div>
              {canWrite && (
                <TableRowActions
                  actions={[
                    {
                      key: 'edit',
                      label: t('editSection'),
                      icon: Pencil,
                      onSelect: () => { setEditingSection(sec); setSectionForm({ name: sec.name, color: sec.color, sortOrder: sec.sortOrder }); },
                      primary: true,
                    },
                    {
                      key: 'delete',
                      label: t('deleteSection', { defaultValue: 'Delete' }),
                      icon: Trash2,
                      onSelect: () => setConfirmDeleteSection(sec.id),
                      variant: 'destructive',
                    },
                  ]}
                />
              )}
            </div>
          ))}

          {canWrite && (
            <div className="rounded-lg border p-4 space-y-3">
              <h4 className="text-sm font-semibold">{editingSection ? t('editSection') : t('addSection')}</h4>
              <Input
                placeholder={t('sectionNamePlaceholder')}
                value={sectionForm.name}
                onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
              />
              <div className="flex items-center gap-3">
                <Label className="text-muted-foreground">{t('colorLabel')}:</Label>
                <input
                  type="color"
                  value={sectionForm.color}
                  onChange={(e) => setSectionForm({ ...sectionForm, color: e.target.value })}
                  className="h-8 w-16 cursor-pointer rounded border"
                />
                <Input
                  type="number"
                  placeholder={t('sortOrder')}
                  className="w-28"
                  value={sectionForm.sortOrder}
                  onChange={(e) => setSectionForm({ ...sectionForm, sortOrder: Number(e.target.value) })}
                />
              </div>
              <div className="flex gap-2">
                {editingSection && (
                  <Button variant="outline" size="sm" onClick={() => { setEditingSection(null); setSectionForm(emptySection); }}>
                    {t('cancel')}
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => saveSectionMutation.mutate(sectionForm)}
                  disabled={saveSectionMutation.isPending || !sectionForm.name}
                >
                  {saveSectionMutation.isPending ? t('saving') : editingSection ? t('update') : t('addSection')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteTable}
        title={t('deleteTableTitle')}
        message={t('deleteTableConfirm', { no: confirmDeleteTable?.tableNo ?? '' })}
        confirmLabel={t('delete')}
        variant="danger"
        loading={deleteTableMutation.isPending}
        onConfirm={() => confirmDeleteTable && deleteTableMutation.mutate(confirmDeleteTable.id)}
        onCancel={() => setConfirmDeleteTable(null)}
      />

      <ConfirmDialog
        open={!!confirmDeleteSection}
        title={t('deleteSectionTitle')}
        message={t('deleteSectionConfirm', { name: sections?.find((s) => s.id === confirmDeleteSection)?.name ?? '' })}
        confirmLabel={t('delete')}
        variant="danger"
        loading={deleteSectionMutation.isPending}
        onConfirm={() => confirmDeleteSection && deleteSectionMutation.mutate(confirmDeleteSection)}
        onCancel={() => setConfirmDeleteSection(null)}
      />
    </div>
  );
}

